import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quote, MapPin, ArrowLeft, HeartHandshake } from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import DonateDialog from "@/components/site/DonateDialog";
import DonateFAB from "@/components/site/DonateFAB";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  {
    id: "amina",
    name: "Amina, 32",
    location: "Dar es Salaam",
    quote:
      "When I felt invisible, this community saw me. The mentorship helped me reopen my tailoring shop and feed my children with dignity.",
    program: "Economic Empowerment",
    image:
      "https://images.pexels.com/photos/6281959/pexels-photo-6281959.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "njeri",
    name: "Njeri, 27",
    location: "Mwanza",
    quote:
      "Raising my son with cerebral palsy alone was crushing. The peer circle and therapy referrals gave me my breath back.",
    program: "Children with Disabilities",
    image:
      "https://images.pexels.com/photos/27499880/pexels-photo-27499880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "fatuma",
    name: "Fatuma, 24",
    location: "Arusha",
    quote:
      "The scholarship let me return to nursing school. Next year I graduate — for me, and for my daughter.",
    program: "Education",
    image:
      "https://images.pexels.com/photos/5386422/pexels-photo-5386422.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

function StoryCard({ s, large = false, dark = false, i = 0 }) {
  return (
    <motion.article
      data-testid={`story-card-${s.id}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: i * 0.05 }}
      className={`group rounded-[28px] overflow-hidden ring-1 ring-brand-border hover-lift ${
        dark ? "bg-brand-navy text-white" : "bg-white"
      } ${large ? "lg:flex" : ""}`}
    >
      <div
        className={`relative overflow-hidden ${
          large ? "lg:w-1/2 h-72 lg:h-auto lg:min-h-[420px]" : "h-72"
        }`}
      >
        <img
          src={s.image}
          alt={s.name}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="font-serif text-2xl">{s.name}</div>
          <div className="text-xs mt-1 flex items-center gap-1.5 text-white/85">
            <MapPin className="h-3.5 w-3.5" />
            {s.location} · {s.program}
          </div>
        </div>
      </div>
      <div className={`p-7 lg:p-9 ${large ? "lg:w-1/2 flex flex-col justify-center" : ""}`}>
        <Quote className="h-7 w-7 text-brand-gold" />
        <p
          className={`mt-3 font-serif italic ${
            large ? "text-2xl lg:text-3xl leading-snug" : "text-lg leading-snug"
          } ${dark ? "text-white" : "text-brand-ink"}`}
        >
          “{s.quote}”
        </p>
      </div>
    </motion.article>
  );
}

export default function StoriesPage() {
  const [stories, setStories] = useState(FALLBACK);
  const [donateOpen, setDonateOpen] = useState(false);
  const openDonate = () => setDonateOpen(true);
  const closeDonate = () => setDonateOpen(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/stories`)
      .then((r) => {
        if (mounted && Array.isArray(r.data) && r.data.length)
          setStories(r.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (donateOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [donateOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const [hero, ...rest] = stories;

  return (
    <div data-testid="stories-page" className="App">
      <Header onDonate={openDonate} />

      <main className="pt-28 lg:pt-32 pb-24">
        {/* Hero strip */}
        <section
          data-testid="stories-page-hero"
          className="relative bg-brand-cream"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <Link
              to="/"
              data-testid="back-to-home-link"
              className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-forest underline-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back home
            </Link>

            <div className="mt-6 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
              <div className="lg:col-span-7">
                <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
                  Stories of strength
                </div>
                <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.02] tracking-tight">
                  In her own{" "}
                  <span className="italic text-brand-forest">words.</span>
                </h1>
              </div>
              <p className="lg:col-span-5 text-brand-muted text-lg leading-relaxed">
                These are not testimonials. They are testimonies — of survival,
                return and rebuilding. Each story is shared with the explicit
                consent of the mother who lived it.
              </p>
            </div>
          </div>
        </section>

        {/* Featured story */}
        {hero && (
          <section className="mt-12 lg:mt-16">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              <StoryCard s={hero} large dark />
            </div>
          </section>
        )}

        {/* Story grid */}
        {rest.length > 0 && (
          <section className="mt-10 lg:mt-14">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {rest.map((s, i) => (
                  <StoryCard key={s.id} s={s} i={i} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Share your story CTA */}
        <section className="mt-20 lg:mt-28">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="rounded-[28px] bg-brand-navy text-white p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-brand-forest/30 blur-3xl" />
              <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-7">
                  <div className="text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold">
                    Your story matters
                  </div>
                  <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
                    Want to share what brought{" "}
                    <span className="italic text-brand-gold">you</span> here?
                  </h2>
                  <p className="mt-4 text-white/80 text-lg leading-relaxed max-w-xl">
                    Whether you found us through a friend, a circle or a hard
                    night — your story can light the way for the next mother.
                    Tell us when you&apos;re ready. No pressure.
                  </p>
                </div>
                <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
                  <Link
                    to="/#need-support"
                    data-testid="share-story-link"
                    className="btn-gold w-full justify-center"
                  >
                    Reach out to us
                  </Link>
                  <button
                    onClick={openDonate}
                    data-testid="stories-page-donate-button"
                    className="btn-outline w-full justify-center !border-white/40 !text-white hover:!bg-white hover:!text-brand-navy"
                  >
                    <HeartHandshake className="h-4 w-4" />
                    Stand with these mothers
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer onDonate={openDonate} />
      <DonateFAB onClick={openDonate} />
      <DonateDialog open={donateOpen} onClose={closeDonate} />
    </div>
  );
}
